import React, { useState, useCallback } from "react";
import { Input, Button, Form, Alert } from "antd";

import LoginLayout from "components/layouts/login-layout/login-layout";
import regex from "constants/regex";
import { useAuth } from "context/AuthContext";
import { useResetPasswordValue } from "state/reactive-vars/reset-password-var";
import { useDoesUserExistLazyQuery } from "graphql/queries/does-user-exist.generated";
import SimpleLoginLayout from "components/layouts/simple-login-layout/simple-login-layout";

const ForgotPassword = () => {
  const [email, setEmail] = useState<string>("");
  const [isResetEmailSent, setIsResetEmailSent] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { startPasswordReset } = useAuth();
  const { updateResetPassword } = useResetPasswordValue();

  const [doesUserExist, { loading: qLoading }] = useDoesUserExistLazyQuery();

  const resetPassword = useCallback(async () => {
    setIsLoading(true);
    setIsResetEmailSent(null);
    try {
      if (startPasswordReset && email) {
        const user = await doesUserExist({
          variables: { email },
        });

        if (!user.data || !user.data.users.length) {
          setIsResetEmailSent(false);
          return;
        }

        const isSuccessful = await startPasswordReset(email);
        setIsResetEmailSent(isSuccessful);
        updateResetPassword({ email });
      }
    } catch (error) {
      console.log(
        "ForgotPassword Firebase start Password Reset error: ",
        error
      );
    } finally {
      setIsLoading(false);
    }
  }, [updateResetPassword, startPasswordReset, email]);

  if (isResetEmailSent) {
    return (
      <SimpleLoginLayout>
        <div className="email-verification-card">
          <div>We've just emailed you a one-time password reset link.</div>
          <div>Please check your inbox or spam folder.</div>

          <span className="link-label">
            Need assistance?
            <a
              className="inline-link bold-link"
              href="mailto:support@highnotes.com?subject=Trouble Signing Up"
            >
              Contact Us
            </a>
          </span>
        </div>
      </SimpleLoginLayout>
    )
  }

  return (
    <LoginLayout title="Forgot your password?" className="forgot-password-screen">
      <div className="forgot-password-container">
        <Form className="login-form" autoComplete="off" onFinish={resetPassword}>
          <Form.Item
            name="email"
            className="login-form-item  forgot-password-space"
            rules={[
              { required: true, message: "Please input your Email." },
              {
                pattern: regex.email,
                message: "Please provide valid email address",
              },
            ]}
            required
            validateTrigger="onBlur"
          >
            <Input
              name="email"
              placeholder="Email"
              className="app-input"
              value={email}
              autoComplete="off"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setEmail(event.target.value);
              }}
              allowClear
            />
          </Form.Item>
          {isResetEmailSent === false && (
            <Alert
              message={<span>We don't recognize that email address.<br />Please try again.</span>}
              className="login-error"
              type="error"
              showIcon
            />
          )}
          {!isResetEmailSent && (
            <Button
              htmlType="submit"
              className="login-layout-button"
              disabled={!email || isLoading || qLoading}
            >
              Reset Password
            </Button>
          )}
        </Form>
        <span className="link-label">
          Trouble Logging In?
          <a
            className="inline-link bold-link"
            href="mailto:support@highnotes.com?subject=Trouble signing in"
          >
            Contact Us
          </a>
        </span>
      </div>
    </LoginLayout>
  );
};

export default ForgotPassword;
